/**
 * Объяснение закрытого режима.
 *
 * Открывается вместо самого режима, когда его условие не выполнено
 * (ТЗ FR-EN-37). Кнопка, которая молча не отвечает, читается как сломанная, а
 * не как запертая, — поэтому нажатие всегда куда-то ведёт. Отсюда же можно
 * сменить язык тренировки на язык недостающей раскладки (ТЗ FR-EN-40):
 * сказать, что откроет режим, и не дать этого сделать — половина ответа.
 *
 * Условие остаётся советом, а не стеной: посмотреть, что там, можно всегда.
 * Запрет «даже не пробуй» учит хуже, чем предупреждение «будет трудно, вот
 * почему»: первое пользователь обходит перезагрузкой и обидой, второе —
 * пониманием.
 */

import type { Translate } from '@type2x/i18n';
import { Button, Card } from '@type2x/ui';

import type { LockedTarget } from '../state/app-state.js';
import type { ScriptReadiness } from '../state/script-readiness.js';

export interface LockedModeScreenProps {
  readonly t: Translate;
  /** Режим, который пытались открыть. */
  readonly target: LockedTarget;
  /** Чего именно не хватает: раскладка и сколько её букв освоено. */
  readonly readiness: ScriptReadiness;
  /** Перейти к тренировке недостающей раскладки. */
  readonly onTrainScript: () => void;
  /** Открыть режим всё равно. */
  readonly onProceed: () => void;
  readonly onBack: () => void;
}

/** Название режима для заголовка: пользователь должен узнать, куда нажимал. */
function modeTitle(t: Translate, target: LockedTarget): string {
  return target.kind === 'packs' ? t(`packs.title.${target.mode}`) : t('layoutSwitch.title');
}

export function LockedModeScreen({
  t,
  target,
  readiness,
  onTrainScript,
  onProceed,
  onBack,
}: LockedModeScreenProps): React.JSX.Element {
  return (
    <Card title={modeTitle(t, target)}>
      <p>
        {t(`modes.locked.${readiness.language}`, {
          mastered: readiness.mastered,
          total: readiness.total,
        })}
      </p>

      <p className="t2x-muted">{t('modes.lockedWhy')}</p>

      <div className="t2x-actions">
        <Button onClick={onTrainScript}>{t(`modes.train.${readiness.language}`)}</Button>
        {/* Второстепенная кнопка, но настоящая: посмотреть, что внутри, можно
            всегда — предупреждение уже прочитано. */}
        <Button variant="secondary" onClick={onProceed}>
          {t('modes.tryAnyway')}
        </Button>
        <Button variant="ghost" onClick={onBack}>
          {t('nav.back')}
        </Button>
      </div>
    </Card>
  );
}
